import React, { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CircularProgress } from '@material-ui/core'
import { applyBasemapProvider, googleHybridProvider } from '../HerbicideSchedule/v3/maricopaBasemap'
import useJobsite, { getJobsiteCoords } from '../SiteTakeoffV3/hooks/useJobsite'
import useGeofence from '../SiteTakeoffV3/hooks/useGeofence'
import usePropertyLines from '../SiteTakeoffV3/hooks/usePropertyLines'
import useAreas from '../SiteTakeoffV3/hooks/useAreas'
import useAreaRendering from '../SiteTakeoffV3/hooks/useAreaRendering'

// Own default center — useMapInit.js's DEFAULT_CENTER is module-private to
// SiteTakeoffV3; this component self-initializes independently.
const DEFAULT_MAP_CENTER = [33.505, -112.15]

// Read-only viewer: property lines + geofence + takeoff areas. No drawing or
// editing — that stays on the full SiteTakeoffV3 page ("Open in Takeoffs").
// The host remounts this component per property (key), so a fast Next/Prev
// tears down in-flight fetches with the whole map instead of guarding
// staleness here.
// Layer visibility (showGeofence/showParcels/showTakeoffs) and zoomNonce are
// host-controlled — the review modal's FAB stack drives them. Bumping
// zoomNonce re-centers on the jobsite after the user pans away.
function PropertyReviewMap ({ url, jobsiteId, showGeofence = true, showParcels = true, showTakeoffs = true, zoomNonce = 0 }) {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const drawnItemsRef = useRef(null)
  const tileLayerRef = useRef(null)

  const { jobsite, loading } = useJobsite(url, jobsiteId)
  // Positional signature (useAreas.js:82), NOT an options object. Read-only:
  // only `areas` is consumed; the hook's save actions are never called.
  const { areas } = useAreas(url, jobsite)

  useEffect(() => {
    if (!mapContainerRef.current) return undefined
    const map = L.map(mapContainerRef.current, { center: DEFAULT_MAP_CENTER, zoom: 17, zoomControl: true })
    applyBasemapProvider({ map, tileLayerRef, provider: googleHybridProvider })
    const drawnItems = new L.FeatureGroup()
    map.addLayer(drawnItems)
    mapRef.current = map
    drawnItemsRef.current = drawnItems
    return () => {
      map.remove()
      mapRef.current = null
      drawnItemsRef.current = null
    }
  }, [])

  useGeofence({ url, jobsite, mapRef, visible: showGeofence })
  // manualOverridesCounty: hand-drawn custom lines exist because the county
  // parcel is wrong — in this read-only viewer they replace the county layer.
  usePropertyLines({ url, jobsite, mapRef, visible: showParcels, parcelInspectorEnabled: false, manualOverridesCounty: true })
  useAreaRendering({ mapRef, drawnItemsRef, areas, visible: showTakeoffs })

  // zoomNonce is a deliberate extra dep: same jobsite, forced re-center.
  useEffect(() => {
    if (!jobsite || !mapRef.current) return
    const coords = getJobsiteCoords(jobsite)
    if (coords) mapRef.current.setView(coords, 18)
  }, [jobsite, zoomNonce])

  if (!jobsiteId) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6f7a6b', fontSize: 14 }}>
        No takeoff site linked
      </div>
    )
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mapContainerRef} data-testid="property-review-map" style={{ width: '100%', height: '100%' }} />
      {loading && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 1000 }}>
          <CircularProgress size={32} />
        </div>
      )}
    </div>
  )
}

export default PropertyReviewMap
